import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Spinner from 'src/components/ui/Features/Spinner/Spinner';
import useFetch from 'src/hook/api/useFetch';
import useUserInfo from 'src/hook/data/useUser';
import usePopup from 'src/hook/modal/usePopUp';
import { HttpMethod, ResponseType } from 'src/model/enum/enums';
import { GetCountryCodeUrl, GetStripeApiUrl, GetWebSiteUrl } from 'src/utils/URLUtils';
import { MembershipPeriod, MembershipPlan, basicPlan } from './MemebershipPlans';
import styles from './Plans.module.scss';
import PlansItem from './PlansItem/PlansItem';
interface PlansProps {
    country: string;
    setCountry: (country: string) => void;
}

const Plans = ({ country, setCountry }: PlansProps) => {
    const { t } = useTranslation();
    const { send } = useFetch();
    const [selectedPlan, setSelectedPlan] = useState<MembershipPeriod>(MembershipPeriod.Yearly);
    const { userInfo } = useUserInfo();
    const [membershipPlans, setMembershipPlans] = useState<Record<MembershipPeriod, MembershipPlan> | null>(null);
    const navigate = useNavigate();
    const { show } = usePopup();

    const fetchCountryPrice = useCallback(async () => {
        const { response } = await send(GetCountryCodeUrl());
        if (response) {
            const countryCode = response as { country: string };
            setCountry(countryCode.country);
            const { response: priceListResponse } = await send(`${GetStripeApiUrl()}/price-list/${countryCode.country}`);

            if (priceListResponse) {
                const priceList = priceListResponse as Array<MembershipPlan>;
                const plans: Record<MembershipPeriod, MembershipPlan> = {
                    [MembershipPeriod.Monthly]: {
                        ...priceList.find(item => item.ProductId === 'ProMonthly')!,
                        Title: 'Monthly'
                    },
                    [MembershipPeriod.Yearly]: {
                        ...priceList.find(item => item.ProductId === 'ProAnnual')!,
                        Title: 'Yearly'
                    },
                    [MembershipPeriod.Lifetime]: {
                        ...priceList.find(item => item.ProductId === 'ProLife')!,
                        Title: 'Lifetime'
                    },
                    [MembershipPeriod.Basic]: basicPlan
                };
                setMembershipPlans(plans);
            }
        }
    }, [send, setCountry]);

    useEffect(() => {
        fetchCountryPrice();
    }, [fetchCountryPrice]);

    const handleContinue = useCallback(async () => {
        if (!membershipPlans) return;

        if (selectedPlan === MembershipPeriod.Basic) {
            navigate('/');
            return;
        }
        var pwindow = window.open('', '_blank');
        pwindow?.document.write('Loading ...');
        pwindow?.focus();

        const { response } = await send(
            selectedPlan === MembershipPeriod.Yearly || selectedPlan === MembershipPeriod.Monthly ?
                `${GetStripeApiUrl()}/stripe-subscription/checkout?autoRedirect=false` :
                `${GetStripeApiUrl()}/checkout?autoRedirect=false`,
            HttpMethod.POST,
            {
                ProductId: membershipPlans[selectedPlan].ProductId,
                Country: country,
                BuyFor: 0,
                WordUpEmail: userInfo?.Email,
                CustomerName: userInfo?.FirstName + ' ' + userInfo?.LastName,
                ReceiptEmail: userInfo?.Email,
                DiscountCode: '',
            },
            ResponseType.Json
        );
        if (response) {
            const url = response as { sessionURL: string };
            pwindow?.location?.assign(url.sessionURL);
        }
    }, [country, userInfo, selectedPlan, send, membershipPlans, navigate]);

    const handleRestorePurchase = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();
        show({
            title: t('Your membership is active.'),
            message: t('Thanks for your trust in wordUp.'),
            showCloseButton: true,
            primaryButtonClick: () => {
                navigate('/');
            }
        });
    }, [navigate, show, t]);


    if (!membershipPlans) {
        return <Spinner fullPage />;
    }

    return (
        <div className={styles.plansContainer}>
            <h2>{t('Choose your Subscription')}</h2>

            {Object.entries(membershipPlans).map(([key, plan]) => (
                <PlansItem
                    key={key}
                    planKey={key as MembershipPeriod}
                    plan={plan}
                    setSelectedPlan={setSelectedPlan}
                    isSelected={selectedPlan === key}
                />
            ))}
            <button className={styles.continueButton} onClick={handleContinue}>
                {t('Continue')}
            </button>
            <div className={styles.linksContainer}>
                <a onClick={(e) => handleRestorePurchase(e)} href="/" className={styles.link}>{t('Restore purchase')}</a>
                <a href={GetWebSiteUrl() + '/terms'} rel="noopener noreferrer" className={styles.link} target="_blank"> ◆ {t('Terms of use')}</a>
                <a href={GetWebSiteUrl() + '/privacy'} rel="noopener noreferrer" className={styles.link} target="_blank"> ◆ {t('Privacy policy')}</a>
            </div>
        </div>
    );
};

export default Plans;
